/* eslint-disable @typescript-eslint/no-use-before-define */
import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react";

import { toast } from "react-toastify";

// Data Sources
import { UserContext } from "components/context/UserContext";

// Components
import { LoaderContext } from "components/context/LoaderContext";

import FeatureCard from "components/atoms/FeatureCard";
import { useDeviceType, useRouter } from "components/hooks";
import { Tab } from "components/atoms/Tab";

import Button from "components/atoms/buttons/Button";
import { BrandSocialCardNew } from "components/molecules/brand/BrandSocialCard";
import clsx from "clsx";
import { getClaimMagnetURL } from "apis/user";
import { getClaimMagnetURL as getSubmitFormURL } from "apis/magnets";
import { post } from "utils/request";
import { Helmet } from "react-helmet-async";
import { MementoModalUI } from "pages/AirdropMemento/PreviewMementoModal";
import DynamicForm from "components/atoms/forms/DynamicForm";
import ClaimMagnetModal from "components/molecules/magnet/ClaimMagnetModal";
import MagnetMementos from "./MagnetMementos";
import MagnetInteractions from "./MagnetInteractions";
import MagnetPublicProfileMementoInteraction from "./MagnetPublicProfileMementoInteraction";
import MagnetActivity from "./MagnetActivity";
import MagnetPublicView from "./MagnetPublicView";
import { ROUTES, SPECIAL_RENDRING_BRAND_IDS, TOAST_CONFIG } from "../../constants";
import MagnetIncentives from "./MagnetIncentives";
import PaymentBreakdownModal from "./PaymentBreakdownModal";

type Props = {
  magnetDetails: PublicMagnet;
  brandWithMagnet: BrandWithMagnets;
  refetchMagnetDetails: () => Promise<any>;
  isMagnetDetailsRefetching: boolean;
  setIsSidebarCustomized: Dispatch<SetStateAction<boolean>>;
};

function MagnetPublicProfile({
  setIsSidebarCustomized,
  magnetDetails,
  brandWithMagnet,
  refetchMagnetDetails,
  isMagnetDetailsRefetching,
}: Props) {
  const [isUserNew, setIsUserNew] = useState(true);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [claimRequestStatus, setClaimRequestStatus] = useState("not-initiated");
  const [dynamicFormSubmitMessage, setDynamicFormSubmitMessage] = useState("");

  const {
    isUserLoggedIn,
    handleBuyWithStripe,
    isMagnetInUserInventory,
    // userDetails,
    // setupUserAccountAndAirDropMagnet,
  } = useContext(UserContext);

  const {
    go,
    query: { eventId },
  } = useRouter();

  const [isMagnetPurchaseModalVisible, setIsMagnetPurchaseModalVisible] = useState(false);
  const [purchaseData, setPurchaseData] = useState<PurchaseData | null>(null);
  const [isCustomizedUIBrand, setIsCustomizedUIBrand] = useState(false);
  const [isDynamicFormModalOpen, setIsDynamicFormModalOpen] = useState(false);

  useEffect(() => {
    const flag = SPECIAL_RENDRING_BRAND_IDS.includes(magnetDetails?.brand_id);
    setIsSidebarCustomized(flag);
    setIsCustomizedUIBrand(flag);
    return () => setIsSidebarCustomized(false);
  }, [magnetDetails?.brand_id]);

  const userPurchaseResolve = useRef<(value: boolean) => void>();

  const acceptPurchase = () => {
    if (userPurchaseResolve.current) {
      userPurchaseResolve.current(true);
    }
  };

  const declinePurchase = () => {
    setPurchaseData(null);
    if (userPurchaseResolve.current) {
      userPurchaseResolve.current(false);
    }
  };

  // @ts-ignore
  const { setLoading: setGlobalLoader } = useContext(LoaderContext);

  const { isMobile } = useDeviceType();

  useEffect(() => {
    if (isMagnetOwnedByUser && window.location.href.split("status=")[1] === "success") {
      setIsMagnetPurchaseModalVisible(true);
    } else if (!isMagnetOwnedByUser && window.location.href.split("status=")[1] === "failure") {
      toast.error("Payment Failed! Please try again to buy Magnet.", TOAST_CONFIG);
    }
  }, []);

  const onPurchaseClickCallback = (modalPurchaseData: PurchaseData) => {
    setPurchaseData(modalPurchaseData);
    setTimeout(declinePurchase, 240000);
    return new Promise<boolean>((resolve) => {
      userPurchaseResolve.current = resolve;
    });
  };

  const submitClaimRequest = async (userResponse: Object) => {
    await post(
      getSubmitFormURL(magnetDetails.id),
      {
        magnet_id: magnetDetails.id,
        event_id: eventId,
        form_response: userResponse,
      },
      {
        processData: (resp: any) => {
          if (userPurchaseResolve.current) {
            userPurchaseResolve.current(true);
            if (isUserLoggedIn) {
              toast.success("Thank you! You responses submitted successfully.", TOAST_CONFIG);
            } else {
              // open another modal
              setClaimRequestStatus("successfull");
              setIsUserNew(resp?.data?.is_new_user);
              setIsSignInModalOpen(true);
            }
            setIsDynamicFormModalOpen(false);
          }
        },
        processError: (resp: any) => {
          if (userPurchaseResolve.current) {
            userPurchaseResolve.current(false);
            if (isUserLoggedIn) {
              toast.error(`Sorry! Failed to submit your responses. ${resp}`, TOAST_CONFIG);
            } else {
              setClaimRequestStatus("failed");
              setIsUserNew(resp?.data?.is_new_user);
              setDynamicFormSubmitMessage(resp);
              setIsSignInModalOpen(true);
            }
          }
        },
      },
    );
  };

  const beforePurchaseClickCallback = async () => {
    if (magnetDetails.claim_form_data_fields?.length && !magnetDetails.is_form_submitted) {
      setIsDynamicFormModalOpen(true);
      return new Promise<boolean>((resolve) => {
        userPurchaseResolve.current = resolve;
      });
    }
    if (!isUserLoggedIn) {
      setIsSignInModalOpen(true);
      return new Promise<boolean>((resolve) => {
        userPurchaseResolve.current = resolve;
      });
    }
    return true;
  };

  const handleMagnetBuy = async () => {
    try {
      const isFormSubmitSuccess = await beforePurchaseClickCallback();
      if (isFormSubmitSuccess) {
        if (isUserLoggedIn) {
          await handleBuyWithStripe(magnetDetails.id, onPurchaseClickCallback);
        }
      }
    } catch (err) {
      toast.error("Something went wrong while buying Magnet", TOAST_CONFIG);
    } finally {
      setGlobalLoader(false);
    }
  };

  const isMagnetOwnedByUser = isMagnetInUserInventory(magnetDetails.id);

  // const handleNavigateToBrandPublicProfile = () => {
  //   go(`${ROUTES.BRAND_PUBLIC_PROFILE}/${brandWithMagnet.id}`);
  // };

  const goToMagnetPageButton = useMemo(
    () => (
      <Button
        text="GO TO MY FRIDGE"
        additionalClasses="sm:px-14"
        variant="magnetiqSolid"
        onClick={() => go(ROUTES.USER_PROFILE_PAGE)}
      />
    ),
    [magnetDetails],
  );

  const handleClaimMagnet = async () => {
    const isFormSubmitSuccess = await beforePurchaseClickCallback();

    // if (!isUserLoggedIn) {
    //   toast.error("You must be logged in to claim magnet", TOAST_CONFIG);
    //   toast.info("Redirecting to login page", { closeButton: true, ...TOAST_CONFIG });
    //   setTimeout(() => {
    //     go(getLoginPageURLWithRedirect());
    //   }, 2000);
    //   return;
    // }

    // if (!userDetails?.is_dapper_setup) {
    //   setupUserAccountAndAirDropMagnet();
    // }

    if (isFormSubmitSuccess && isUserLoggedIn) {
      post(
        getClaimMagnetURL(magnetDetails.id),
        {
          event_id: eventId,
        },
        {
          processData: () => {
            setIsMagnetPurchaseModalVisible(true);
            toast.success("Magnet claimed. You will see it reflected on your Fridge soon!", TOAST_CONFIG);
          },
          processError: (errorMessage) =>
            toast.error(errorMessage || "Something went wrong while you tried to claim you Magnet", TOAST_CONFIG),
        },
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${magnetDetails.name} - ${magnetDetails.brand_name} - MAGNETIQ`}</title>
      </Helmet>
      <div className={clsx(isCustomizedUIBrand && "bg-[#e6e7e8]")}>
        <MementoModalUI
          isMagnetView
          mementoName="Congratulations!"
          isFileAudioOrVideo={false}
          open={isMagnetPurchaseModalVisible}
          magnetName={magnetDetails.name}
          magnetImage={magnetDetails.image_url}
          mediaUrl={magnetDetails.image_url}
          actionButtons={goToMagnetPageButton}
          brandName={magnetDetails.brand_name}
          onClose={() => setIsMagnetPurchaseModalVisible(false)}
          isFileImage
          mementoDesc={`You have acquired "${magnetDetails.name}". Once the order is settled you will still see it on your Fridge!`}
        />
        {magnetDetails.claim_form_data_fields && !magnetDetails.is_form_submitted && (
          <DynamicForm
            isOpen={isDynamicFormModalOpen}
            fields={magnetDetails.claim_form_data_fields}
            isCustomizedUIBrand={isCustomizedUIBrand}
            handleFormSubmit={submitClaimRequest}
            onClose={() => {
              setIsDynamicFormModalOpen(false);
            }}
          />
        )}

        <ClaimMagnetModal
          isUserNew={isUserNew}
          isOpen={isSignInModalOpen}
          onClose={() => {
            setIsSignInModalOpen(false);
          }}
          submissionStatus={claimRequestStatus}
          errorMessage={dynamicFormSubmitMessage}
        />

        <PaymentBreakdownModal
          purchaseData={purchaseData as PurchaseData}
          handleAcceptPurchase={acceptPurchase}
          handleDeclinePurchase={declinePurchase}
        />

        <div className="relative z-10 h-[40vh] bg-red-500">
          <img
            className="absolute   h-full w-full object-cover"
            src={brandWithMagnet.cover_image_url || ""}
            alt={brandWithMagnet.name}
          />
          {/* {!isCustomizedUIBrand && ( */}
          <div
            className={clsx(
              "absolute left-0 top-0  h-full w-full bg-gradient-to-t",
              isCustomizedUIBrand ? "from-[#e6e7e8]" : "from-primary",
            )}
          />
          {/* )} */}
        </div>
        <div className="mx-2 min-h-[90vh] max-w-[1500px]  gap-y-4 pb-10 sm:mx-5 xl:mx-10 4xl:mx-auto 4xl:px-10">
          {/* Brand Card */}
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <BrandSocialCardNew {...brandWithMagnet} textDark={isCustomizedUIBrand} />
          <div className="mt-6 font-sora">
            <div className="grid grid-cols-12 gap-x-4">
              <MagnetPublicView
                magnetDetails={magnetDetails}
                refetchMagnetDetails={refetchMagnetDetails}
                handleMagnetBuy={handleMagnetBuy}
                isMagnetDetailsRefetching={isMagnetDetailsRefetching}
                handleClaimMagnet={handleClaimMagnet}
                // Check if the magnet is event exclusive and if there is no event id in the url then restrict the claim.
                // relay on backend to verify the correctness of the event id.
                restrictMagnetClaim={!!(magnetDetails.event_exclusive && !eventId)}
                isMagnetOwnedByUser={isMagnetOwnedByUser}
              />
            </div>
            <div className="mt-8 grid grid-cols-12 gap-y-4 md:gap-x-8 2xl:gap-x-20 2xl:gap-y-8">
              {isMobile ? (
                <MagnetMobileView
                  isMagnetOwnedByUser={isMagnetOwnedByUser}
                  magnetId={magnetDetails.id}
                  magnetBenefits={magnetDetails.benefits}
                  brandId={magnetDetails.brand_id}
                />
              ) : (
                <MagnetDesktopView
                  isMagnetOwnedByUser={isMagnetOwnedByUser}
                  magnetId={magnetDetails.id}
                  brandId={magnetDetails.brand_id}
                  magnetBenefits={magnetDetails.benefits}
                  pollsResponded={magnetDetails.responded_polls_ids || []}
                  socialShareResponded={magnetDetails.social_share_responded_ids || []}
                />
              )}
            </div>
            <div className="col-span-12">{/* <Footer /> */}</div>
          </div>
        </div>
      </div>
    </>
  );
}
export default MagnetPublicProfile;

type MagnetViewProps = {
  isMagnetOwnedByUser: boolean;
  magnetId: string;
  magnetBenefits: MagnetBenefit[];
  pollsResponded?: string[];
  socialShareResponded?: string[];
  brandId?: string;
};

function MagnetMobileView({
  brandId,
  isMagnetOwnedByUser,
  magnetId,
  magnetBenefits,
  pollsResponded = [],
  socialShareResponded = [],
}: MagnetViewProps) {
  const [isCustomizedUIBrand, setIsCustomizedUIBrand] = useState(false);

  useEffect(() => {
    setIsCustomizedUIBrand(SPECIAL_RENDRING_BRAND_IDS.includes(brandId || ""));
  }, [brandId]);
  return (
    <div className="col-span-12 pb-2 ">
      <Tab fullBorder fullWidthTabs className="mt-4 w-full justify-between border-b border-white border-opacity-40">
        {/* Mementos */}
        <Tab.TabPane
          key="Mementos"
          tab={isCustomizedUIBrand ? "Education Bank" : "Mementos"}
          className={`relative pb-1 pt-3 text-center ${isCustomizedUIBrand ? "text-primary" : "text-white"}`}
        >
          <div className="max-h-[70vh] overflow-y-scroll">
            <MagnetMementos isMobile isMagnetOwnedByUser={!!isMagnetOwnedByUser} />
          </div>
        </Tab.TabPane>

        {/* Poll */}
        <Tab.TabPane
          key="Poll"
          tab={isCustomizedUIBrand ? "Surveys" : "Influence"}
          className={`relative pb-1 pt-3 text-center ${isCustomizedUIBrand ? "text-primary" : "text-white"}`}
        >
          <div className="max-h-[70vh] overflow-y-scroll">
            <MagnetInteractions isCustomizedUIBrand={isCustomizedUIBrand} />
          </div>
        </Tab.TabPane>

        {/* Incentives */}
        <Tab.TabPane
          key="Incentives"
          tab={isCustomizedUIBrand ? "Benefits" : "Incentives"}
          className={`relative pb-1 pt-3 text-center ${isCustomizedUIBrand ? "text-primary" : "text-white"}`}
        >
          <div className="max-h-[70vh] overflow-y-scroll">
            <MagnetIncentives
              isMobile
              pollsResponded={pollsResponded}
              socialShareResponded={socialShareResponded}
              isCustomizedUIBrand={isCustomizedUIBrand}
            />
          </div>
        </Tab.TabPane>

        {/* Activity */}
        {!isCustomizedUIBrand && (
          <Tab.TabPane
            key="Activity"
            tab="Activity"
            className={`relative pb-1 pt-3 text-center ${isCustomizedUIBrand ? "text-primary" : "text-white"}`}
          >
            <div className="max-h-[70vh] overflow-y-scroll py-2">
              <MagnetActivity magnetId={magnetId} />
            </div>
          </Tab.TabPane>
        )}
      </Tab>
      <div className="magnet-benefits-grid pb-12">
        <div className={`py-4 text-sm font-bold ${isCustomizedUIBrand ? "text-primary" : "text-white"}`}>
          Program Details
        </div>
        <div className="flex flex-col space-y-3">
          {magnetBenefits.map((benefit) => (
            <div
              key={`mag_feature_mobile${benefit.title}`}
              // className="relative col-span-1 h-44 rounded-2xl border-opacity-10 hover:overflow-visible 2xl:h-52"
            >
              <FeatureCard
                imageHash={benefit.image_hash}
                isMobile
                featureImage={benefit.image_url}
                title={benefit.title}
                description={benefit.description}
                textDark={isCustomizedUIBrand}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MagnetDesktopView({
  isMagnetOwnedByUser,
  magnetId,
  brandId,
  magnetBenefits,
  pollsResponded = [],
  socialShareResponded = [],
}: MagnetViewProps & { brandId: string }) {
  const [isCustomizedUIBrand, setIsCustomizedUIBrand] = useState(false);

  useEffect(() => {
    setIsCustomizedUIBrand(SPECIAL_RENDRING_BRAND_IDS.includes(brandId));
  }, [brandId]);

  return (
    <>
      <div className={clsx(isMagnetOwnedByUser ? "order-1" : "order-2", "col-span-12 grid grid-cols-12 gap-x-6")}>
        <div className={clsx("col-span-12 object-cover", isCustomizedUIBrand ? "lg:col-span-12" : "lg:col-span-7")}>
          <MagnetPublicProfileMementoInteraction
            isCustomizedUIBrand={isCustomizedUIBrand}
            isMagnetOwnedByUser={isMagnetOwnedByUser}
            pollsResponded={pollsResponded}
            socialShareResponded={socialShareResponded}
          />
        </div>
        {isCustomizedUIBrand ? null : (
          <div className=" w-full object-cover lg:col-span-5">
            <MagnetActivity magnetId={magnetId} />
          </div>
        )}
      </div>

      {/* Magnet Features */}
      <div
        className={clsx(
          isMagnetOwnedByUser ? "order-2 mb-10" : "order-1",
          "magnet-benefits-grid col-span-12 hidden rounded-2xl border-1 p-4 pb-8 md:block",
          isCustomizedUIBrand ? "border-[#842572]" : "border-secondary",
        )}
      >
        {magnetBenefits.length > 0 && (
          <>
            <p
              className={clsx(
                "mx-2 text-sm font-bold 2xl:mt-3 2xl:text-xl",
                isCustomizedUIBrand ? "text-primary" : "text-white",
              )}
            >
              Program Details
            </p>
            <div className="mt-4 grid  grid-cols-2 gap-x-6 gap-y-4">
              {magnetBenefits.map((benefit, idx) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`mag_feature${benefit.title}${idx}`}
                  className="relative col-span-1 h-44 rounded-2xl border-opacity-10 hover:overflow-visible xl:h-28 "
                >
                  <FeatureCard
                    imageHash={benefit.image_hash}
                    featureImage={benefit.image_url}
                    title={benefit.title}
                    description={benefit.description}
                    containerClasses="border-none"
                    horizontal
                    textDark={isCustomizedUIBrand}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
